/**
 * WorkItem（条目）API — 统一 L1 apiClient
 */
import type { AxiosRequestConfig } from 'axios'

import { ProjectType, TaskPurpose } from '@/api/common-types'
import type { BatchRequestConfig } from '@/shared/api/use-api-batch'
import { request } from '@/shared/api/request'

export { ProjectType, TaskPurpose }

/** WorkItem 派生任务状态（兼容） */
export type WorkItemStatus =
  | 'no_task'
  | 'pending'
  | 'processing'
  | 'submitted'
  | 'completed'
  | 'rejected_at_assignment'
  | string

/** 条目流水线阶段（与后端 WorkItemPipelineStage 对齐） */
export type WorkItemPipelineStage =
  | 'uploaded'
  | 'designing'
  | 'notes_ready'
  | 'machine_running'
  | 'subtitles_ready'
  | 'workspace_ready'
  | 'awaiting_review'
  | 'in_review'
  | 'completed'
  | 'failed'

export type WorkItemRequestConfig = AxiosRequestConfig | BatchRequestConfig

export interface WorkItemTaskSummary {
  id: number
  status: string
  creator_id: number
  assignee_id?: number | null
  /** 当前执行人摘要（列表展示） */
  assignee?: {
    id: number
    username: string
    name: string
    name_remark?: string | null
    language?: string | null
  } | null
  round: number
  task_purpose?: string | null
}

/** 活跃审校派生摘要（count = distinct assignee） */
export interface ActiveReviewAssigneeSummary {
  user_id: number
  name: string
  active_task_count: number
  target_languages?: string[]
  statuses?: string[]
}

export interface ActiveReviewSummary {
  count: number
  assignees: ActiveReviewAssigneeSummary[]
}

/** 语言对摘要（教案：全 Task version distinct 目的语） */
export interface LanguagePairSummary {
  source_language?: string | null
  target_languages: string[]
  target_count: number
}

export interface WorkItem {
  id: number
  project_id: number
  item_type: ProjectType
  file_name?: string | null
  source_language?: string | null
  target_language?: string | null
  creator_id: number
  current_task_id?: number | null
  pipeline_stage?: WorkItemPipelineStage | string
  /** @deprecated 目录勿用此字段冒充制品进度；优先 pipeline_stage */
  status: WorkItemStatus
  /** @deprecated 多 Task 时勿作唯一执行人；优先 active_review_summary */
  current_task?: WorkItemTaskSummary | null
  active_review_summary?: ActiveReviewSummary | null
  language_pair_summary?: LanguagePairSummary | null
  created_at?: string | null
  completed_at?: string | null
  file_url?: string | null
  file_size?: number | null
  file_type?: string | null
  file_storage_id?: number | null
  translated_document_storage_id?: number | null
  translated_document_url?: string | null
  video_file_storage_id?: number | null
  audio_file_storage_id?: number | null
  original_subtitle_storage_id?: number | null
  translated_subtitle_storage_id?: number | null
  subtitle_url?: string | null
  video_duration?: number | null
  character_count?: number | null
}

export interface WorkItemListResponse {
  items: WorkItem[]
  total: number
}

export interface WorkItemCreateRequest {
  project_id: number
  item_type: ProjectType
  target_language?: string | null
  file_name?: string
  source_language?: string
  /** @deprecated 创建时不再自动建 Task */
  assignee_id?: number
  /** @deprecated 创建时不再自动建 Task */
  task_purpose?: TaskPurpose
}

export function aggregateWorkItemStatusCounts(
  items: WorkItem[],
): Record<string, number> {
  const counts: Record<string, number> = {}
  for (const item of items) {
    const key = normalizePipelineStageForDisplay(
      item.item_type,
      item.pipeline_stage || item.status || 'unknown',
    )
    counts[key] = (counts[key] ?? 0) + 1
  }
  return counts
}

/** 历史委托态 stage → 制品就绪桶（与后端 normalize 对齐） */
export function normalizePipelineStageForDisplay(
  itemType: ProjectType | string | undefined,
  stage: string,
): string {
  if (stage !== 'awaiting_review' && stage !== 'in_review') return stage
  return itemType === ProjectType.LESSON || itemType === 'lesson'
    ? 'notes_ready'
    : 'workspace_ready'
}

export interface FolderStatusSummary {
  total: number
  completed: number
  submitted: number
  processing: number
}

export function summarizeFolderStatusCounts(
  counts: Record<string, number>,
): FolderStatusSummary {
  const total = Object.values(counts).reduce((sum, n) => sum + n, 0)
  // awaiting_review / in_review already mapped into ready buckets by aggregate
  return {
    total,
    completed: counts.completed ?? 0,
    submitted: counts.submitted ?? 0,
    processing:
      (counts.processing ?? 0) +
      (counts.machine_running ?? 0) +
      (counts.subtitles_ready ?? 0) +
      (counts.designing ?? 0),
  }
}

export async function getWorkItems(
  projectId: number,
  config?: WorkItemRequestConfig,
): Promise<WorkItemListResponse> {
  return request.get<WorkItemListResponse>('/work-items/', {
    ...config,
    params: { project_id: projectId },
  })
}

export async function getWorkItem(
  workItemId: number,
  config?: WorkItemRequestConfig,
): Promise<WorkItem> {
  return request.get<WorkItem>(`/work-items/${workItemId}`, config)
}

export async function createWorkItem(
  data: WorkItemCreateRequest,
  config?: WorkItemRequestConfig,
): Promise<WorkItem> {
  return request.post<WorkItem>('/work-items/', data, config)
}

export async function deleteWorkItem(
  workItemId: number,
  config?: WorkItemRequestConfig,
): Promise<{ message: string }> {
  return request.delete<{ message: string }>(`/work-items/${workItemId}`, config)
}

export interface WorkItemTaskAssignRequest {
  assignee_id: number
}

export interface WorkItemTaskAssignResponse {
  success: boolean
  message: string
  task_id: number
  work_item_id: number
}

export interface WorkItemCompleteResponse {
  work_item_id: number
  pipeline_stage: WorkItemPipelineStage | string
  completed_at?: string | null
  report_triggered: boolean
}

/** 指派校验任务（机翻就绪后） */
export async function assignReviewTask(
  workItemId: number,
  data: WorkItemTaskAssignRequest,
  config?: WorkItemRequestConfig,
): Promise<WorkItemTaskAssignResponse> {
  return request.post<WorkItemTaskAssignResponse>(
    `/work-items/${workItemId}/assign-review`,
    data,
    config,
  )
}

/** @deprecated 请优先使用 assignReviewTask */
export async function assignWorkItemTask(
  workItemId: number,
  data: WorkItemTaskAssignRequest,
  config?: WorkItemRequestConfig,
): Promise<WorkItemTaskAssignResponse> {
  return request.post<WorkItemTaskAssignResponse>(
    `/work-items/${workItemId}/assign-task`,
    data,
    config,
  )
}

/** 完成项目（当前条目，无外派 Task） */
export async function completeWorkItem(
  workItemId: number,
  config?: WorkItemRequestConfig,
): Promise<WorkItemCompleteResponse> {
  return request.post<WorkItemCompleteResponse>(
    `/work-items/${workItemId}/complete`,
    {},
    config,
  )
}

export const workItemApi = {
  getWorkItems,
  getWorkItem,
  createWorkItem,
  deleteWorkItem,
  assignReviewTask,
  assignWorkItemTask,
  completeWorkItem,
  aggregateWorkItemStatusCounts,
  summarizeFolderStatusCounts,
}

export default workItemApi
